import { ref } from 'vue';
import axios from 'axios';
import { t } from '../i18n';
import { isWorseWhenHigher } from '../utils/stat_directions.js';
import { compareStatToAverage, hasRankAverage } from '../utils/stat_comparison.js';
import { mapUnitType, getUnitType } from './useCoachData.js';

// Module-level cache: one in-flight promise, one resolved value. The resolved
// value is a ref so breakdowns re-resolve once the definitions arrive.
const statDefinitions = ref(null);
let definitionsPromise = null;
const warnedKeys = new Set();

const LINEAR_OPS = new Set(['sum', 'difference']);
const ARITHMETIC_OPS = new Set(['ratio', 'sum', 'difference', 'weighted_sum']);
const OP_SYMBOLS = { ratio: '÷', sum: '+', difference: '−' };

// kast is stored 0-1 since rsparse e247e16 (2026-05-29); values above 1 are
// old-era 0-100 rows, whichever payload they arrive on.
const PERCENTAGE_100_STATS = new Set(['kast']);

// An operand only counts as materially limiting when its relative shortfall
// beats the next worst by more than this.
const LIMITING_MARGIN = 0.10;

export function fetchStatDefinitions() {
  if (!definitionsPromise) {
    definitionsPromise = axios.get('/coach/stat_definitions')
      .then(({ data }) => {
        statDefinitions.value = data || {};
        return statDefinitions.value;
      })
      .catch((err) => {
        definitionsPromise = null; // allow a retry after a failed fetch
        throw err;
      });
  }
  return definitionsPromise;
}

// The shared definitions ref, fetched on first use: already filled on a
// remount, null while loading or after a failed fetch.
export function useStatDefinitions() {
  fetchStatDefinitions().catch(() => {});
  return statDefinitions;
}

export function resetStatDefinitionsCache() {
  statDefinitions.value = null;
  definitionsPromise = null;
  warnedKeys.clear();
}

function toFinite(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

// weapon_damage on the match payload is a bare number, not a {value} object.
function readOperand(entry, source) {
  if (typeof entry === 'number') return Number.isFinite(entry) ? entry : null;
  if (entry == null || typeof entry !== 'object') return null;
  return toFinite(source === 'match' ? entry.value : entry.players_value);
}

// 0 is the backend's "no peer data" placeholder, never a real average.
function readPeerAverage(entry, source) {
  if (entry == null || typeof entry !== 'object') return null;
  const raw = source === 'match' ? entry.match_average : entry.rating_range_average_value;
  return hasRankAverage(raw) ? Number(raw) : null;
}

// An old-era 0-100 kast value has the recipe's ×100 already baked in; a
// modern 0-1 value takes the scale as served. The value decides the era.
function effectiveScale(input, value) {
  const scale = input.scale ?? 1;
  if (PERCENTAGE_100_STATS.has(input.key) && value > 1) return 1;
  return scale;
}

function normalizeForDisplay(key, value, unitType) {
  if (value == null) return value;
  if (PERCENTAGE_100_STATS.has(key) && unitType === 'percentage' && value > 1) return value / 100;
  return value;
}

function operandUnitType(input, entry) {
  if (input.unit_type) return mapUnitType(input.unit_type);
  if (entry && typeof entry === 'object' && entry.unit_type) return mapUnitType(entry.unit_type);
  return getUnitType(input.key || '');
}

// Roles can repeat (impact's assists + flash assists share 'numerator'), so
// each side sums its inputs; positional fallback when a side has no role.
function ratioDenominator(inputs, values) {
  let denominator = 0;
  let denominatorCount = 0;
  inputs.forEach((input, index) => {
    if (input.role === 'denominator') {
      denominator += values[index];
      denominatorCount += 1;
    }
  });
  return denominatorCount === 0 ? values[1] : denominator;
}

function ratioFromValues(inputs, values) {
  let numerator = 0;
  let numeratorCount = 0;
  inputs.forEach((input, index) => {
    if (input.role === 'numerator') {
      numerator += values[index];
      numeratorCount += 1;
    }
  });
  if (numeratorCount === 0) numerator = values[0];
  const result = numerator / ratioDenominator(inputs, values);
  return Number.isFinite(result) ? result : null;
}

// Weapon career stats divide by rounds_with_weapon, not rounds_played, and
// the merged weapon map shortens the counter-strafe count names.
const WEAPON_KEY_ALIASES = {
  rounds_played: { key: 'rounds_with_weapon', label: 'rounds_with_weapon' },
  accurate_shots_while_counterstrafing: { key: 'accurate_counterstrafing', label: 'accurate_counterstrafing', renamed: true },
  total_shots_while_counterstrafing: { key: 'total_counterstrafing', label: 'total_counterstrafing', renamed: true },
};

function operandKeyFor(input, source) {
  const alias = source === 'weapon' ? WEAPON_KEY_ALIASES[input.key] : null;
  if (!alias) return { key: input.key, label: input.label ?? input.key, renamed: false };
  return { key: alias.key, label: t(`shared.stat_breakdown.${alias.label}`), renamed: alias.renamed === true };
}

// ratio / sum / difference over plain operand keys, used for nested derived
// inputs like hltv_rating's deaths-per-round term.
function computeSimple(op, inputs, operandSource, source) {
  const values = (inputs || []).map((input) => readOperand(operandSource?.[operandKeyFor(input, source).key], source));
  if (values.length === 0 || values.some((value) => value == null)) return null;
  switch (op) {
    case 'ratio':
      return ratioFromValues(inputs, values);
    case 'sum':
      return values.reduce((total, value) => total + value, 0);
    case 'difference':
      return values.slice(1).reduce((total, value) => total - value, values[0]);
    default:
      return null;
  }
}

function derivedFormula(derived) {
  const inputs = derived.inputs || [];
  const label = (input) => input.label ?? input.key;
  if (derived.op === 'ratio') {
    const roled = (role, fallback) => {
      const matches = inputs.filter((input) => input.role === role);
      return matches.length > 0 ? matches : fallback;
    };
    // Each side sums its inputs and falls back positionally, so bracket the
    // sums rather than chaining ÷ and follow the same fallbacks.
    const top = roled('numerator', inputs.slice(0, 1)).map(label);
    const bottom = roled('denominator', inputs.slice(1, 2)).map(label);
    if (top.length > 0 && bottom.length > 0) {
      const term = (parts) => (parts.length > 1 ? `(${parts.join(' + ')})` : parts[0]);
      return `${term(top)} ÷ ${term(bottom)}`;
    }
  }
  const symbol = OP_SYMBOLS[derived.op] || '+';
  return inputs.map(label).join(` ${symbol} `);
}

function buildOperand(input, operandSource, source, definitions) {
  if (input.derived) {
    const value = computeSimple(input.derived.op, input.derived.inputs, operandSource, source);
    return {
      key: input.key ?? null,
      label: input.label ?? input.key,
      value,
      displayValue: value,
      unit_type: input.unit_type ? mapUnitType(input.unit_type) : 'decimal',
      coefficient: input.coefficient ?? null,
      peerAverage: null,
      peerDelta: null,
      worseWhenHigher: false,
      shortfall: null,
      limiting: false,
      subFormula: input.formula ?? derivedFormula(input.derived),
      note: input.note ?? null,
      derived: true,
    };
  }

  const { key, label, renamed } = operandKeyFor(input, source);
  const entry = operandSource?.[key];
  const value = readOperand(entry, source);
  const unitType = operandUnitType({ ...input, key }, entry);
  const worseWhenHigher = isWorseWhenHigher(key, entry);
  const peerAverage = readPeerAverage(entry, source);

  let peerDelta = null;
  let shortfall = null;
  if (peerAverage != null && value != null) {
    const comparison = compareStatToAverage(value, peerAverage, !worseWhenHigher);
    if (comparison) peerDelta = { percent: comparison.percentDifference, better: comparison.percentDifference >= 0 };
    // relative shortfall in the harmful direction; 0 when at or beyond peers
    const harmfulGap = worseWhenHigher ? value - peerAverage : peerAverage - value;
    shortfall = Math.max(0, harmfulGap / Math.abs(peerAverage));
  }

  return {
    key,
    label,
    value,
    displayValue: normalizeForDisplay(key, value, unitType),
    unit_type: unitType,
    coefficient: input.coefficient ?? null,
    peerAverage,
    peerDelta,
    worseWhenHigher,
    shortfall,
    limiting: false,
    subFormula: definitions?.[key]?.formula ?? (renamed ? definitions?.[input.key]?.formula : null) ?? null,
    note: input.note ?? null,
    derived: false,
  };
}

function computeComposite(recipe, operands) {
  if (operands.length === 0 || operands.some((operand) => operand.value == null)) return null;

  if (recipe.op === 'weighted_sum') {
    let total = recipe.constant ?? 0;
    (recipe.inputs || []).forEach((input, index) => {
      total += (input.coefficient ?? 1) * effectiveScale(input, operands[index].value) * operands[index].value;
    });
    return Number.isFinite(total) ? total : null;
  }

  const values = operands.map((operand) => operand.value);
  switch (recipe.op) {
    case 'ratio':
      return ratioFromValues(recipe.inputs || [], values);
    case 'sum':
      return values.reduce((total, value) => total + value, 0);
    case 'difference':
      return values.slice(1).reduce((total, value) => total - value, values[0]);
    default:
      return null;
  }
}

// Averaging is why a profile ratio drifts; it cannot be why a sum does, so the two
// cases need different words rather than one wrong footer or no footer at all.
function footerFor(recipe, source, sign) {
  if (LINEAR_OPS.has(recipe.op)) {
    return sign === '\u2248' ? t('shared.stat_breakdown.sum_footer') : null;
  }
  if (source !== 'profile' || sign === '=') return null;
  return t('shared.stat_breakdown.ratio_footer');
}

function markLimiting(operands) {
  const candidates = operands.filter((operand) => operand.shortfall != null);
  if (candidates.length === 0) return;
  const sorted = [...candidates].sort((a, b) => b.shortfall - a.shortfall);
  const worst = sorted[0];
  const nextWorst = sorted[1]?.shortfall ?? 0;
  if (worst.shortfall > 0 && worst.shortfall - nextWorst > LIMITING_MARGIN) worst.limiting = true;
}

function warnExactnessGap(statKey, value, computedValue) {
  if (!import.meta.env.DEV || warnedKeys.has(statKey)) return;
  warnedKeys.add(statKey);
  console.warn(
    `[useStatBreakdown] ${statKey}: recomputed ${computedValue} differs from displayed ${value} by >0.5% where they should match`,
  );
}

// The merged weapon-filtered map falls back to profile entries for keys the
// merge doesn't produce; those operands must hide the breakdown, not mix scopes.
function allOperandsWeaponScoped(recipe, operandSource) {
  return (recipe.inputs || []).every((input) => {
    const leaves = input.derived ? input.derived.inputs || [] : [input];
    return leaves.every((leaf) => operandSource?.[operandKeyFor(leaf, 'weapon').key]?.weapon_scoped === true);
  });
}

// Resolves a stat's breakdown from the recipe registry against a payload; null
// (breakdown hidden) when the recipe or a required operand is missing.
export function resolveBreakdown(statKey, { source, values } = {}) {
  const definitions = statDefinitions.value;
  const recipe = definitions?.[statKey];
  if (!recipe || !values || !source) return null;

  const operandSource = values;
  if (source === 'weapon' && !allOperandsWeaponScoped(recipe, operandSource)) return null;

  const hasArithmetic = ARITHMETIC_OPS.has(recipe.op);
  const operands = (recipe.inputs || []).map((input) => buildOperand(input, operandSource, source, definitions));

  // Arithmetic ops need every operand; model/share_of_rounds only list inputs.
  if (hasArithmetic && (operands.length === 0 || operands.some((operand) => operand.value == null))) return null;

  const unitType = recipe.unit_type
    ? mapUnitType(recipe.unit_type)
    : operandUnitType({ key: statKey }, operandSource[statKey]);
  const computedValue = hasArithmetic ? computeComposite(recipe, operands) : null;
  let value = readOperand(operandSource[statKey], source);
  if (value == null) value = computedValue;
  if (value == null) return null;

  const exactContext = source === 'match' || source === 'weapon';
  // Averaging is linear: a mean of sums still adds back, only means of ratios drift.
  const addsBackExactly = exactContext || LINEAR_OPS.has(recipe.op);
  // computedValue is null when nothing qualified (a 0 denominator): the rows
  // and notes still render, but no equals line claims arithmetic we can't do.
  let sign = null;
  if (hasArithmetic && computedValue != null) {
    if (!addsBackExactly) {
      sign = '≈';
    } else {
      const reference = Math.max(Math.abs(value), Math.abs(computedValue));
      const gap = reference === 0 ? 0 : Math.abs(computedValue - value) / reference;
      if (gap > 0.005) {
        sign = '≈';
        warnExactnessGap(statKey, value, computedValue);
      } else {
        sign = '=';
      }
    }
  }

  markLimiting(operands);

  return {
    key: statKey,
    label: recipe.label ?? statKey,
    op: recipe.op,
    formula: recipe.formula ?? null,
    note: recipe.note ?? null,
    unit_type: unitType,
    source,
    exactContext,
    sign,
    value,
    displayValue: normalizeForDisplay(statKey, value, unitType),
    computedValue,
    operands,
    limitingKey: operands.find((operand) => operand.limiting)?.key ?? null,
    footer: footerFor(recipe, source, sign),
  };
}

// refrag_rating lists ~30 inputs but only 5 fit, and the limiting one is usually
// past the cut — swap it into the last slot so the badge can't point at a hidden row.
const MAX_OPERANDS = 5;
export function visibleOperands(operands) {
  if (operands.length <= MAX_OPERANDS) return operands;

  const shown = operands.slice(0, MAX_OPERANDS);
  const limiting = operands.find((operand) => operand.limiting);
  if (limiting && !shown.includes(limiting)) shown[MAX_OPERANDS - 1] = limiting;
  return shown;
}
